import { z } from "zod";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import {
  getSelectedValue,
  hasOverlappingDosisRanges,
  isDosisUnidad,
  parseDecimalInput,
} from "../../utils/service-master-form";

const dosisRangeSchema = z.object({
  id: z.number().optional(),
  modalidad: z.any().optional(),
  dosisDesde: z.string().optional(),
  dosisHasta: z.string().optional(),
  valorUnitario: z.string().optional(),
});

export const serviceMasterCreationSchema = z
  .object({
    titulo: z.string().trim().min(1, "El título es obligatorio."),
    categoria: z.any().refine((value) => Boolean(getSelectedValue(value)), {
      message: "La categoría es obligatoria.",
    }),
    unidadCotizacion: z.any().refine((value) => Boolean(getSelectedValue(value)), {
      message: "El tipo de cotización es obligatorio.",
    }),
    descripcion: z.string().optional(),
    valorUnitario: z.string().optional(),
    activo: z.any().optional().nullable(),
    cotizacionesDosis: z.array(dosisRangeSchema).optional(),
  })
  .superRefine((values, ctx) => {
    if (!isDosisUnidad(values.unidadCotizacion)) {
      if (!formatCurrencyInputToNumber(values.valorUnitario ?? "")) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "El valor unitario es obligatorio.",
          path: ["valorUnitario"],
        });
      }
      return;
    }

    const rows = values.cotizacionesDosis ?? [];

    if (!rows.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Debe definir al menos un rango de dosis.",
        path: ["cotizacionesDosis"],
      });
      return;
    }

    const parsedRows = rows.map((row, index) => {
      const modalidad = getSelectedValue(row?.modalidad);
      const desde = parseDecimalInput(row?.dosisDesde);
      const hasta = parseDecimalInput(row?.dosisHasta);
      const valorUnitario = formatCurrencyInputToNumber(row?.valorUnitario ?? "");

      if (!modalidad) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La modalidad es obligatoria.",
          path: ["cotizacionesDosis", index, "modalidad"],
        });
      }
      if (desde === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Campo obligatorio.",
          path: ["cotizacionesDosis", index, "dosisDesde"],
        });
      }
      if (hasta === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Campo obligatorio.",
          path: ["cotizacionesDosis", index, "dosisHasta"],
        });
      }
      if (!valorUnitario) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Campo obligatorio.",
          path: ["cotizacionesDosis", index, "valorUnitario"],
        });
      }
      if (desde !== undefined && hasta !== undefined && desde >= hasta) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "El valor 'Desde' debe ser menor que el valor 'Hasta' en todos los rangos.",
          path: ["cotizacionesDosis"],
        });
      }

      return { modalidad, desde, hasta };
    });

    if (hasOverlappingDosisRanges(parsedRows)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Los rangos de la misma modalidad no pueden superponerse.",
        path: ["cotizacionesDosis"],
      });
    }
  });

export type ServiceMasterCreationForm = z.infer<
  typeof serviceMasterCreationSchema
>;
